<script lang="ts">
  import {
    currentDoc,
    currentDocId,
    displayedDoc,
    displayedDocId,
    displayingDoc,
    editingMode,
    focus,
    leftNavWidth,
    plugins,
    preferences,
    projectMetas,
    projectStore,
    rightPaneTotalWidth,
    settings,
    showLeftPane,
    showRightPane,
    size,
    t,
    viewport,
    writing,
  } from '@dabble/app';
  import { EditorElement } from '@dabble/data/editables';
  import { Focus } from '@dabble/data/stores/focus';
  import { DocSettings } from '@dabble/data/types';
  import Icon from '@dabble/toolkit/Icon.svelte';
  import Interactions from '@dabble/toolkit/Interactions.svelte';
  import { autoscroll } from '@dabble/toolkit/autoscroll';
  import { addListener } from '@dabble/toolkit/listen';
  import { pagepad } from '@dabble/toolkit/pagepad';
  import { dbltap, tap } from '@dabble/toolkit/tap';
  import { mdiPencil } from '@mdi/js';
  import { SvelteComponent, onMount, tick } from 'svelte';
  import { Editor, TextDocument } from 'typewriter-editor';
  import StatusBar from './StatusBar.svelte';

  export let loading = true;
  let workspace: HTMLElement;
  let docSettings: DocSettings;
  let hideStatusBar: boolean;
  let justFocused = false;
  let currentView: SvelteComponent;
  let sidebarSizeDifference = 0;
  let workspaceWidth = 1000;
  let noBackground = false;

  const { showCoauthors } = plugins.stores;

  $: docSettings = $currentDoc && $settings[$currentDoc.type];
  $: hideStatusBar = docSettings && docSettings.hideStatusBar;
  $: setJustFocused($focus);
  $: viewport.container.set(workspace || null);
  $: if (!loading && $currentDoc) onDocDisplayed();
  $: if ($editingMode === 1) {
    removeSelection();
  }
  $: paddingLeft = ($size === 'desktop' && $showLeftPane && $leftNavWidth) || 0;
  $: paddingRight = ($size === 'desktop' && $showRightPane && $rightPaneTotalWidth) || 0;
  $: focusPadLeft = sidebarSizeDifference < 0 ? -sidebarSizeDifference : 0;
  $: focusPadRight = sidebarSizeDifference > 0 ? sidebarSizeDifference : 0;
  $: if ($currentDocId) noBackground = false;

  onMount(() => {
    const selChangeUnsub = addListener(workspace, 'editor-change', onEditorChange);
    $writing = false;
    focus.stop();
    return () => {
      selChangeUnsub();
      viewport.container.set(null);
      onDocDisplayed();
    };
  });

  function removeSelection() {
    const selection = workspace && workspace.ownerDocument && workspace.ownerDocument.getSelection();
    if (!selection || !selection.anchorNode) return;
    if (workspace.contains(selection.anchorNode)) {
      selection.removeAllRanges();
    }
  }

  function onDocDisplayed() {
    if (!$currentDoc) {
      $displayingDoc = false;
      if ($displayedDoc) $displayedDoc = null;
      if ($displayedDocId) $displayedDocId = null;
    } else {
      $displayingDoc = true;
      $displayedDoc = $currentDoc;
      const docId = ($currentDoc && $currentDoc.id) || null;
      if ($displayedDocId !== docId) $displayedDocId = docId;
    }
  }

  async function setJustFocused(focus: Focus) {
    if (focus.focused || focus.autofocused) {
      justFocused = true;
      // Fixes Safari's trigger of a phantom mousemove event
      setTimeout(() => (justFocused = false), 100);
    }

    if (focus.focused && workspace.scrollWidth === workspace.clientWidth) {
      sidebarSizeDifference = paddingRight - paddingLeft;
    } else if (sidebarSizeDifference) {
      sidebarSizeDifference = 0;
    }
  }

  function onInput({ target }: Event) {
    if (!$writing) $writing = true;

    // Handle auto-focus
    if (
      $preferences.noAutofade ||
      docSettings.disableFocusMode ||
      $focus.focusing ||
      $focus.focused ||
      $focus.autofocused ||
      !(target as HTMLElement).closest('.page')
    )
      return;
    focus.start();
  }

  let mousePos: { x: number; y: number };
  function onMouseMove({ detail: event }: CustomEvent<MouseEvent>) {
    if ($focus.autofocused) {
      // Leave autofocus if the mouse is moved substantially, but not if it moves a pixel or two
      if (!mousePos) {
        mousePos = { x: event.clientX, y: event.clientY };
      } else if (Math.abs(event.clientX - mousePos.x) + Math.abs(event.clientY - mousePos.y) > 10) {
        onStopWriting();
      }
    } else {
      onStopWriting();
    }
  }

  function onStopWriting() {
    if (justFocused) return;
    $writing = false;
    mousePos = null;
    focus.stop();
  }

  async function startEdit() {
    $editingMode = 2;
    await tick();
    viewport.selectNearTop();
  }

  async function onEdit({ relatedTarget, pageX, pageY }: MouseEvent) {
    if (!$editingMode) return;
    let related = relatedTarget as Element;
    if (related.nodeType !== Node.ELEMENT_NODE) related = related.parentElement;
    const editorElement = related.closest && related.closest('.typewriter-editor');
    const editor = editorElement && (editorElement as EditorElement).editor;
    if (!editor) return;
    const field = editor.identifier;
    // find the closest selection for the x/y, scroll it to be within the to 1/3 of the viewport, and select it
    const index = editor.getIndexFromPoint(pageX, pageY);
    $editingMode = 2;
    await tick();
    viewport.select(field, [index, index], true);
  }

  async function onEditorChange({
    detail: { editor, old, doc },
  }: CustomEvent<{ editor: Editor; old: TextDocument; doc: TextDocument }>) {
    if ($editingMode !== 2) return;
    if (old.selection || !doc.selection) return;
    const field = editor.identifier;
    // find the closest selection for the x/y, scroll it to be within the to 1/3 of the viewport, and select it
    viewport.scrollIntoView(field, doc.selection[0], true);
  }

  function onMouseScroll(event: WheelEvent) {
    if (event && event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey && event.deltaY) {
      event.preventDefault();
      workspace.scrollLeft += event.deltaY;
    }
  }
</script>

<div
  class="project-screen"
  class:focused={$focus.focused || $focus.autofocused}
  class:focusing={$focus.focusing}
  class:focus-slowly={$focus.focusing && !$focus.focusLock}
  class:show-author={$showCoauthors}
  style:background-color={(!noBackground && currentView?.backgroundColor) || 'transparent'}
  style:padding-left="{paddingLeft}px"
  style:padding-right="{paddingRight}px"
  on:wheel={onMouseScroll}
>
  <div
    bind:this={workspace}
    id="project-container"
    class="project-container focus-scroll {$currentDoc && $currentDoc.type}"
    style="padding-left:{focusPadLeft}px;padding-right:{focusPadRight}px;--workspace-width:{workspaceWidth}px"
    use:tap
    use:dbltap
    use:pagepad
    use:autoscroll={!$preferences.noScrollLock}
    on:dbltap={$editingMode && onEdit}
    on:editorinput={onInput}
    bind:offsetWidth={workspaceWidth}
  >
    {#if !loading}
      {#if $currentDoc}
        {#if docSettings.view}
          <svelte:component
            this={docSettings.view.component}
            doc={$currentDoc}
            bind:this={currentView}
            bind:noBackground
          />
        {/if}
      {:else if $projectStore.project}
        <div class="document-not-found">
          {$t('not_found_doc')}
        </div>
      {:else if $projectMetas[$projectStore.projectId]}
        <div class="document-not-found">
          {$t('not_found_project_data')}
        </div>
      {:else}
        <div class="document-not-found">
          {$t('not_found_project')}
        </div>
      {/if}
    {/if}
  </div>
</div>

{#if !hideStatusBar && $editingMode !== 2}
  <div
    class="status-container"
    class:focused={$focus.focused || $focus.autofocused}
    class:focusing={$focus.focusing}
    class:focus-slowly={$focus.focusing && !$focus.focusLock}
  >
    <StatusBar {paddingLeft} {paddingRight} />
  </div>
{/if}

{#if $editingMode === 1}
  <button class="btn info large edit-button" use:tap on:tap={startEdit}>
    <Icon path={mdiPencil} />
  </button>
{/if}

<Interactions on:mousemove={onMouseMove} on:touchstart={onStopWriting} on:shortcut-Escape={onStopWriting} />

<style>
  .project-screen {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    height: 100%;
    width: 100%;
    overflow: hidden;
    contain: strict;
  }
  .project-container {
    flex: 1 1 100%;
    cursor: auto;
    border: none;
    -webkit-overflow-scrolling: touch;
    overflow: auto;
    overflow-y: scroll;
  }
  .status-container {
    position: relative;
  }
  .document-not-found {
    margin-top: 100px;
    text-align: center;
    font-weight: bold;
    font-size: 36px;
    color: rgba(0, 0, 0, 0.5);
    text-shadow: var(--text-highlight);
  }
  .edit-button.btn.large {
    position: fixed;
    display: flex;
    font-size: 32px;
    padding: 16px;
    right: 32px;
    bottom: 80px;
    border-radius: 100px;
    box-shadow: var(--global-edit-button-shadow);
  }
</style>
